import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import { AsYouTypeFormatter } from 'google-libphonenumber/dist/libphonenumber';
import { FormattedMessage } from 'react-intl';
import {selectInput, textInput} from './FormInput';
import { Screens, pruneObj } from './constants';
import MessageDefinitions from './MessageDefinitions';
import MessageIfPresent from '@ignitesales/neuro-application/src/components/MessageIfPresent';

const ContactForm = ({useAppointmentFlow, isProcessing, contactLead, onContactFormSubmit, onComplete, changeScreen, fields = ContactForm.defaultConsumerFields, schema = ContactForm.defaultConsumerSchema}) => {
  const formRef = useRef(null);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [form, setForm] = useState(contactLead);

  const validateForm = (state) => {
    const prunedState = pruneObj(state);
    if(formRef.current && formRef.current.validate) {
      formRef.current.validate(prunedState).then((formErrors = []) => {
        const errors = formErrors.map(error => {
          return {
            key: error.path,
            errors: [error.message]
          };
        });

        setErrors(errors);
      });
    }
  };

  const valueChanged = (state) => {
    const lead = { ...state };
    if (lead.phone) {
      lead.phone = state.phone ? state.phone.replace(/\D/g, '') : '';
    }
    validateForm(lead);
    setForm(lead);
  };

  const formSubmitted = (state) => {
    const lead = { ...state };
    lead.phone = state.phone ? state.phone.replace(/\D/g, '') : '';

    onContactFormSubmit(lead);
    if(!useAppointmentFlow){
      onComplete({type: 'contact'});
    }else {
      changeScreen(Screens.APPT_LOCATION);
    }
  };

  const formValid = !errors.length;
  const value = pruneObj(form);

  if (value.phone) {
    const formatter = new AsYouTypeFormatter('US');
    let phoneNumber = null;

    for (const n of value.phone || []) {
      phoneNumber = formatter.inputDigit(n);
    }

    value.phone = phoneNumber;
  }

  const fieldErrors = {};

  errors.forEach(error => {
    fieldErrors[error.key] = true;
  });

  const formFields = fields.map(field => {
    let type = 'text';
    switch (field) {
      case 'email':
        type = 'email';
        break;

      case 'phone':
        type = 'tel';
        break;

      case 'preferredTime':
        return selectInput({
          name: field,
          options: {
            morning: 'Morning',
            afternoon: 'Afternoon',
            evening: 'Evening',
            noPreference: 'No preference'
          },
          labelMessage: MessageDefinitions[field],
          placeholderMessage: MessageDefinitions[`${field}Placeholder`],
          errorMessage: MessageDefinitions[`${field}Error`],
          hasError: showErrors && fieldErrors[field]
        });

      default:
        break;
    }
    return textInput({
      type,
      name: field,
      labelMessage: MessageDefinitions[field],
      placeholderMessage: MessageDefinitions[`${field}Placeholder`],
      errorMessage: MessageDefinitions[`${field}Error`],
      hasError: showErrors && fieldErrors[field]
    });
  });

  const buttonMessage =
    isProcessing
      ? <FormattedMessage{...MessageDefinitions.loading} />
      : useAppointmentFlow
        ? <FormattedMessage {...MessageDefinitions.continue} />
        : <FormattedMessage {...MessageDefinitions.submit} />;

  const disclosureMessage =
    <MessageIfPresent {...MessageDefinitions.formDisclosure}>
      <div className="form-disclosure">
        <FormattedMessage {...MessageDefinitions.formDisclosure} tagName='span' />
      </div>
    </MessageIfPresent>;

  return (
    <Validate schema={schema} ref={formRef}>
      <Form value={value} className="contactMe__form" onChange={valueChanged} onSubmit={formSubmitted}>
        <div className="form-body">
          {formFields}
        </div>
        {disclosureMessage}
        <div className="form-actions" onClick={() => setShowErrors(true)}>
          <button type="submit" className="form-submitBtn" disabled={!formValid || isProcessing}>
            {buttonMessage}
          </button>
        </div>
      </Form>
    </Validate>
  );
};

ContactForm.propTypes = {
  contactLead: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.string),
  schema: PropTypes.object,
  useAppointmentFlow: PropTypes.bool,
  onContactFormSubmit: PropTypes.func,
  contactMeScreenChanged: PropTypes.func
};

ContactForm.defaultBusinessSchema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1
    },
    lastName: {
      type: 'string',
      minLength: 1
    },
    businessName: {
      type: 'string',
      minLength: 1
    },
    email: {
      type: 'string',
      format: 'email'
    },
    phone: {
      type: 'string',
      'pattern': '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$'
    },
    customerId: {
      type: 'string',
      minLength: 1
    },
    preferredTime: {
      type: 'string',
      enum: ['morning','afternoon','evening','noPreference']
    }
  },
  required: ['firstName', 'lastName', 'email', 'phone', 'businessName']
};

ContactForm.defaultConsumerSchema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1
    },
    lastName: {
      type: 'string',
      minLength: 1
    },
    email: {
      type: 'string',
      format: 'email'
    },
    phone: {
      type: 'string',
      'pattern': '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$'
    },
    customerId: {
      type: 'string',
      minLength: 1
    },
    preferredTime: {
      type: 'string',
      enum: ['morning','afternoon','evening','noPreference']
    }
  },
  required: ['firstName', 'lastName', 'email', 'phone']
};

ContactForm.defaultConsumerFields = ['firstName', 'lastName', 'email', 'phone'];
ContactForm.defaultBusinessFields = ['firstName', 'lastName', 'businessName', 'email', 'phone'];

export default ContactForm;
